import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/utils';
import { FileText, ArrowRight, CheckCircle2, Clock, Send } from 'lucide-react';

interface QuotePipelineData {
  total: number;
  preparation: number;
  sent: number;
  approved: number;
  invoiced?: number;
  preparationValue?: string;
  sentValue?: string;
  approvedValue?: string;
  invoicedValue?: string;
  totalValue?: string;
}

interface QuotePipelineCardProps {
  quotes: QuotePipelineData;
  onNavigateToTab: (tab: string) => void;
}

export default function QuotePipelineCard({
  quotes,
  onNavigateToTab,
}: QuotePipelineCardProps) {
  // Calculate conversion rate: (approved + invoiced) / total sent+approved+invoiced
  const closedQuotes = (quotes.approved || 0) + (quotes.invoiced || 0);
  const totalProcessed = (quotes.sent || 0) + closedQuotes;
  const conversionRate = totalProcessed > 0
    ? Math.round((closedQuotes / totalProcessed) * 100)
    : 0;

  // Parse values (they come as strings from the API)
  const preparationValue = parseFloat(quotes.preparationValue || '0') || 0;
  const sentValue = parseFloat(quotes.sentValue || '0') || 0;
  const approvedValue = parseFloat(quotes.approvedValue || '0') || 0;
  const invoicedValue = parseFloat(quotes.invoicedValue || '0') || 0;

  // Check if there's any quote activity
  const hasQuotes = quotes.total > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Quote Pipeline
            </CardTitle>
            <CardDescription>Active quotes by stage</CardDescription>
          </div>
          {hasQuotes && (
            <Badge variant="outline" className={conversionRate >= 50 ? 'text-green-600 border-green-300' : 'text-muted-foreground'}>
              {conversionRate}% conversion
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasQuotes ? (
          <>
            {/* Pipeline Stages */}
            <div className="space-y-2">
              {/* Preparation */}
              <PipelineStage
                icon={<Clock className="h-3.5 w-3.5" />}
                label="In Preparation"
                count={quotes.preparation}
                value={preparationValue}
                color="text-gray-600"
                bgColor="bg-gray-100"
              />

              {/* Arrow */}
              <div className="flex justify-center">
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
              </div>

              {/* Sent */}
              <PipelineStage
                icon={<Send className="h-3.5 w-3.5" />}
                label="Sent / Awaiting"
                count={quotes.sent}
                value={sentValue}
                color="text-blue-600"
                bgColor="bg-blue-50"
              />

              {/* Arrow */}
              <div className="flex justify-center">
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
              </div>

              {/* Approved */}
              <PipelineStage
                icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                label="Approved"
                count={quotes.approved}
                value={approvedValue}
                color="text-green-600"
                bgColor="bg-green-50"
              />

              {/* Invoiced (if any) */}
              {(quotes.invoiced || 0) > 0 && (
                <>
                  <div className="flex justify-center">
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <PipelineStage
                    icon={<FileText className="h-3.5 w-3.5" />}
                    label="Invoiced"
                    count={quotes.invoiced || 0}
                    value={invoicedValue}
                    color="text-purple-600"
                    bgColor="bg-purple-50"
                  />
                </>
              )}
            </div>

            {/* Total Value */}
            <div className="pt-2 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Pipeline Value</span>
                <span className="font-semibold">
                  {formatCurrency(preparationValue + sentValue + approvedValue)}
                </span>
              </div>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-sm text-center py-2">
            No active quotes
          </p>
        )}

        {/* View All Link */}
        <button
          onClick={() => onNavigateToTab('quotes')}
          className="text-xs text-primary hover:underline"
        >
          View all quotes &rarr;
        </button>
      </CardContent>
    </Card>
  );
}

interface PipelineStageProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  value: number;
  color: string;
  bgColor: string;
}

function PipelineStage({ icon, label, count, value, color, bgColor }: PipelineStageProps) {
  return (
    <div className={`flex items-center justify-between p-2 rounded-md ${bgColor}`}>
      <div className={`flex items-center gap-2 ${color}`}>
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <Badge variant="secondary" className={`h-5 ${color} bg-white/80`}>
          {count}
        </Badge>
        {value > 0 && (
          <span className={`text-xs font-medium ${color}`}>
            {formatCurrency(value)}
          </span>
        )}
      </div>
    </div>
  );
}
